import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Validators, FormBuilder, FormGroup, AbstractControl } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CountdownTimerStyles, TIMER_LAYOUT_ENUM } from '../../../models/countdown-timer';

const MAXIMUM_FILE_SIZE = 2; // MB

@Component({ selector: 'lib-text-options', templateUrl: './text-options.component.html', styleUrls: ['./text-options.component.scss'] })
export class TextOptionsComponent implements OnInit,
  OnChanges,
  OnDestroy {
  @Input() data: CountdownTimerStyles;
  @Output() handleSubmit = new EventEmitter();
  @Output() handleFormValid = new EventEmitter();

  @ViewChild('fileInput', { static: false })fileInput: ElementRef;
  public frm: FormGroup;
  public fonts = [];

  private _destroy$: Subject<void>;

  constructor(private _fb: FormBuilder) {
    this._destroy$ = new Subject();
    this._createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue) {
      this.fonts = this._getFontsByLayout(changes.data.currentValue.layout);
      this._formMapping(changes.data.currentValue);
    }
  }

  ngOnInit() {
    this._subscriptions();
  }

  ngOnDestroy() {
    this
      ._destroy$
      .next();
    this
      ._destroy$
      .complete();
  }

  get f() {
    if (!this.frm) {
      return {};
    }
    return this.frm.controls;
  }

  getErrorMessage(control: AbstractControl) {
    if (!control.errors) {
      return '';
    }

    if (control.errors.required) {
      return 'This field is required.';
    }

    return 'Invalid field';
  }

  onChangeFontColor($event) {
    this
      .f
      .fontColor
      .setValue($event);
  }

  onChangeBackgroundColor($event) {
    this
      .f
      .backgroundColor
      .setValue($event);
  }

  onUploadFile(files) {
    if (files.length === 0) {
      return;
    }

    const fileSize = files[0].size / 1024 / 1024; // MB
    if (fileSize > MAXIMUM_FILE_SIZE) {
      alert(`the maximum file size is ${MAXIMUM_FILE_SIZE}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this
        .f
        .backgroundImage
        .setValue(reader.result);
    };

    reader.readAsDataURL(files[0]);
  }

  onClearImage() {
    this.f.backgroundImage.setValue('');
    this.fileInput.nativeElement.value = '';
  }

  private _createForm() {
    this.frm = this
      ._fb
      .group({
        backgroundColor: [
          '#ffffff',
          [Validators.required]
        ],
        backgroundImage: ['', []
        ],
        fontColor: [
          '#000000',
          [Validators.required]
        ],
        fontFamily: [
          'Arial',
          [Validators.required]
        ],
        fontSize: [
          70,
          [Validators.required]
        ]
      });
  }

  private _formMapping(data: CountdownTimerStyles) {
    if (!data) {
      return;
    }

    const { backgroundColor, backgroundImage, fontColor, fontFamily, fontSize } = data;

    this
      .f
      .backgroundColor
      .setValue(backgroundColor);
    this
      .f
      .backgroundImage
      .setValue(backgroundImage);
    this
      .f
      .fontColor
      .setValue(fontColor);
    this
      .f
      .fontFamily
      .setValue(fontFamily);
    this
      .f
      .fontSize
      .setValue(fontSize);
  }

  private _subscriptions() {
    this
      .frm
      .valueChanges
      .pipe(debounceTime(100), takeUntil(this._destroy$))
      .subscribe(() => {
        this._onUpdateFormValid();
      });
  }

  private _onUpdateFormValid() {
    const isValid = this.frm.valid;

    if (isValid) {
      this._onSubmit();
    } else {
      this
        .handleSubmit
        .emit(null);
    }
  }

  private _onSubmit() {
    this
      .handleSubmit
      .emit({
        ...this._composeData()
      });
  }

  private _composeData() {
    const { backgroundColor, backgroundImage, fontColor, fontFamily, fontSize } = this.frm.value;

    const payload = new CountdownTimerStyles();

    payload.backgroundColor = backgroundColor;
    payload.backgroundImage = backgroundImage;
    payload.fontColor = fontColor;
    payload.fontFamily = fontFamily;
    payload.fontSize = fontSize;
    payload.layout = this.data.layout || TIMER_LAYOUT_ENUM.default;
    payload.customLayoutAllowed = this.data.customLayoutAllowed || true;

    return payload;
  }

  private _getFontsByLayout(layout: TIMER_LAYOUT_ENUM) {
    let fonts = [
      {
        alias: 'Arial',
        name: 'Arial'
      }, {
        alias: 'Roboto',
        name: 'Roboto'
      }, {
        alias: 'Tahoma',
        name: 'Tahoma'
      }, {
        alias: 'Ubuntu Mono',
        name: 'UbuntuMono'
      }, {
        alias: 'Roboto Mono',
        name: 'RobotoMono'
      }, {
        alias: 'PT Mono',
        name: 'PTMono'
      }
    ];

    switch (layout) {
      case TIMER_LAYOUT_ENUM.overlay:
        fonts = [{
            alias: 'Roboto',
            name: 'Roboto'
          }, {
            alias: 'Tahoma',
            name: 'Tahoma'
          }, {
            alias: 'Verdana',
            name: 'Verdana'
          }, {
            alias: 'Ubuntu Mono',
            name: 'UbuntuMono'
          }, {
            alias: 'Roboto Mono',
            name: 'RobotoMono'
          }, {
            alias: 'PT Mono',
            name: 'PTMono'
          }, {
            alias: 'Courier Prime',
            name: 'CourierPrime'
          }
        ];
        break;
      case TIMER_LAYOUT_ENUM.modern:
        fonts = [
          {
            alias: 'Arial',
            name: 'Arial'
          }, {
            alias: 'Roboto',
            name: 'Roboto'
          }
        ];
        break;
      default:
    }

    return fonts;
  }
}
